// inkfmt/v1 JSON serialization. See FORMAT.md.
import {
  CHANNELS, FORMAT_ID, STRIDE, TEMPLATES,
  type BaseRecord, type DeviceInfo, type EpochMs, type Exported, type HoverSegment,
  type Notebook, type Page, type Stroke,
} from '../model/types';

type Json<T> = Omit<Exported<T & BaseRecord>, 'points'> & { points: number[] };

export type StrokeJson = Json<Stroke>;
export type HoverJson = Json<HoverSegment>;
export interface PageJson extends Exported<Page> {
  strokes: StrokeJson[];
  hover: HoverJson[];
}
export interface NotebookJson extends Exported<Notebook> {
  pages: PageJson[];
}
export interface InkDocument {
  format: typeof FORMAT_ID;
  kind: 'notebook' | 'backup';
  exportedAt: EpochMs;
  exportedBy?: DeviceInfo;
  notebooks: NotebookJson[];
}

/** Flat record sets, as stored (minus rev). */
export interface RecordSet {
  notebooks: Exported<Notebook>[];
  pages: Exported<Page>[];
  strokes: Exported<Stroke>[];
  hover: Exported<HoverSegment>[];
}

// ---------- serialize ----------

/** Shortest decimal that parses back to the same float32. */
export function f32(v: number): number {
  if (!Number.isFinite(v) || Number.isInteger(v)) return v;
  for (let p = 1; p < 9; p++) {
    const n = Number(v.toPrecision(p));
    if (Math.fround(n) === v) return n;
  }
  return Number(v.toPrecision(9));
}

export function pointsToJson(points: Float32Array): number[] {
  const out = new Array<number>(points.length);
  for (let i = 0; i < points.length; i++) out[i] = f32(points[i]!);
  return out;
}

function stripRev<T extends BaseRecord>(r: T): Exported<T> {
  const { rev: _rev, ...rest } = r;
  return rest;
}

function runToJson<T extends Stroke | HoverSegment>(r: T): Json<T> {
  const { rev: _rev, points, ...rest } = r;
  return { ...rest, points: pointsToJson(points) } as unknown as Json<T>;
}

export function buildDocument(
  set: { notebooks: Notebook[]; pages: Page[]; strokes: Stroke[]; hover: HoverSegment[] },
  kind: InkDocument['kind'],
  exportedBy?: DeviceInfo,
  exportedAt: EpochMs = Date.now(),
): InkDocument {
  const byCreated = <T extends { createdAt: number; startTime?: number }>(a: T, b: T) =>
    (a.startTime ?? a.createdAt) - (b.startTime ?? b.createdAt);
  const strokesByPage = groupBy(set.strokes, (s) => s.pageId);
  const hoverByPage = groupBy(set.hover, (h) => h.pageId);
  const pagesByNb = groupBy(set.pages, (p) => p.notebookId);

  const notebooks = [...set.notebooks].sort(byCreated).map((nb): NotebookJson => ({
    ...stripRev(nb),
    pages: (pagesByNb.get(nb.id) ?? [])
      .sort((a, b) => a.index - b.index)
      .map((p): PageJson => ({
        ...stripRev(p),
        strokes: (strokesByPage.get(p.id) ?? []).sort(byCreated).map(runToJson),
        hover: (hoverByPage.get(p.id) ?? []).sort(byCreated).map(runToJson),
      })),
  }));
  return { format: FORMAT_ID, kind, exportedAt, ...(exportedBy ? { exportedBy } : {}), notebooks };
}

export const stringifyDocument = (doc: InkDocument): string => JSON.stringify(doc);

function groupBy<T>(items: T[], key: (t: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const it of items) {
    const k = key(it);
    const arr = m.get(k);
    if (arr) arr.push(it);
    else m.set(k, [it]);
  }
  return m;
}

// ---------- parse ----------

export class FormatError extends Error {
  constructor(public path: string, msg: string) {
    super(`${path}: ${msg}`);
    this.name = 'FormatError';
  }
}

type Obj = Record<string, unknown>;

function obj(v: unknown, path: string): Obj {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) throw new FormatError(path, 'expected object');
  return v as Obj;
}
function arr(v: unknown, path: string): unknown[] {
  if (!Array.isArray(v)) throw new FormatError(path, 'expected array');
  return v;
}
function str(o: Obj, k: string, path: string): string {
  const v = o[k];
  if (typeof v !== 'string') throw new FormatError(`${path}.${k}`, 'expected string');
  return v;
}
function num(o: Obj, k: string, path: string): number {
  const v = o[k];
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new FormatError(`${path}.${k}`, 'expected finite number');
  return v;
}
function optNum(o: Obj, k: string, path: string): number | undefined {
  return o[k] === undefined ? undefined : num(o, k, path);
}
function idOrNull(o: Obj, k: string, path: string): string | null {
  return o[k] === null ? null : str(o, k, path);
}
function oneOf<T extends string>(o: Obj, k: string, allowed: readonly T[], path: string): T {
  const v = str(o, k, path);
  if (!(allowed as readonly string[]).includes(v)) throw new FormatError(`${path}.${k}`, `expected one of ${allowed.join('|')}`);
  return v as T;
}

function base(o: Obj, path: string) {
  const deletedAt = optNum(o, 'deletedAt', path);
  return {
    id: str(o, 'id', path),
    createdAt: num(o, 'createdAt', path),
    updatedAt: num(o, 'updatedAt', path),
    ...(deletedAt !== undefined ? { deletedAt } : {}),
  };
}

function parseRun(o: Obj, path: string, pageId: string, notebookId: string) {
  if (o.pageId !== pageId) throw new FormatError(`${path}.pageId`, 'does not match parent page');
  if (o.notebookId !== notebookId) throw new FormatError(`${path}.notebookId`, 'does not match parent notebook');
  const ch = arr(o.channels, `${path}.channels`);
  if (ch.length !== STRIDE || ch.some((c, i) => c !== CHANNELS[i])) {
    throw new FormatError(`${path}.channels`, `expected ${JSON.stringify(CHANNELS)}`);
  }
  const raw = arr(o.points, `${path}.points`);
  if (raw.length % STRIDE !== 0) throw new FormatError(`${path}.points`, `length not a multiple of ${STRIDE}`);
  const points = new Float32Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    const v = raw[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) throw new FormatError(`${path}.points[${i}]`, 'expected finite number');
    points[i] = v;
  }
  const cap = obj(o.capture, `${path}.capture`);
  return {
    pageId,
    notebookId,
    deviceId: str(o, 'deviceId', path),
    pointerType: oneOf(o, 'pointerType', ['pen', 'mouse', 'touch'] as const, path),
    startTime: num(o, 'startTime', path),
    channels: CHANNELS,
    points,
    capture: {
      zoom: num(cap, 'zoom', `${path}.capture`),
      offsetX: num(cap, 'offsetX', `${path}.capture`),
      offsetY: num(cap, 'offsetY', `${path}.capture`),
    },
  };
}

function parseDevice(v: unknown, path: string): DeviceInfo {
  const o = obj(v, path);
  const s = obj(o.screen, `${path}.screen`);
  return {
    id: str(o, 'id', path),
    userAgent: str(o, 'userAgent', path),
    devicePixelRatio: num(o, 'devicePixelRatio', path),
    screen: {
      width: num(s, 'width', `${path}.screen`),
      height: num(s, 'height', `${path}.screen`),
      availWidth: num(s, 'availWidth', `${path}.screen`),
      availHeight: num(s, 'availHeight', `${path}.screen`),
    },
    maxTouchPoints: num(o, 'maxTouchPoints', path),
    recordedAt: num(o, 'recordedAt', path),
  };
}

/** Parses and validates an inkfmt/v1 document into flat record sets. Unknown fields are kept. */
export function parseDocument(text: string): { doc: Pick<InkDocument, 'format' | 'kind' | 'exportedAt'>; records: RecordSet } {
  let root: unknown;
  try {
    root = JSON.parse(text);
  } catch (e) {
    throw new FormatError('$', `invalid JSON (${(e as Error).message})`);
  }
  const r = obj(root, '$');
  if (r.format !== FORMAT_ID) throw new FormatError('$.format', `expected "${FORMAT_ID}", got ${JSON.stringify(r.format)}`);
  const kind = oneOf(r, 'kind', ['notebook', 'backup'] as const, '$');
  const exportedAt = num(r, 'exportedAt', '$');

  const records: RecordSet = { notebooks: [], pages: [], strokes: [], hover: [] };
  arr(r.notebooks, '$.notebooks').forEach((nbv, ni) => {
    const np = `$.notebooks[${ni}]`;
    const nb = obj(nbv, np);
    const { pages: pagesRaw, rev: _r, ...nbRest } = nb;
    const devicesRaw = obj(nb.devices, `${np}.devices`);
    const devices: Record<string, DeviceInfo> = {};
    for (const [k, v] of Object.entries(devicesRaw)) devices[k] = parseDevice(v, `${np}.devices.${k}`);
    if (nb.format !== FORMAT_ID) throw new FormatError(`${np}.format`, `expected "${FORMAT_ID}"`);
    const notebook: Exported<Notebook> = {
      ...(nbRest as object),
      ...base(nb, np),
      format: FORMAT_ID,
      title: str(nb, 'title', np),
      devices,
      defaultTemplate: oneOf(nb, 'defaultTemplate', TEMPLATES, np),
    };
    records.notebooks.push(notebook);

    arr(pagesRaw, `${np}.pages`).forEach((pv, pi) => {
      const pp = `${np}.pages[${pi}]`;
      const p = obj(pv, pp);
      const { strokes: strokesRaw, hover: hoverRaw, rev: _pr, ...pRest } = p;
      if (p.notebookId !== notebook.id) throw new FormatError(`${pp}.notebookId`, 'does not match parent notebook');
      const size = obj(p.size, `${pp}.size`);
      const page: Exported<Page> = {
        ...(pRest as object),
        ...base(p, pp),
        notebookId: notebook.id,
        index: num(p, 'index', pp),
        template: oneOf(p, 'template', TEMPLATES, pp),
        size: { width: num(size, 'width', `${pp}.size`), height: num(size, 'height', `${pp}.size`) },
      };
      records.pages.push(page);

      arr(strokesRaw, `${pp}.strokes`).forEach((sv, si) => {
        const sp = `${pp}.strokes[${si}]`;
        const s = obj(sv, sp);
        const { rev: _sr, ...sRest } = s;
        let transform: Stroke['transform'];
        if (s.transform !== undefined) {
          const t = arr(s.transform, `${sp}.transform`);
          if (t.length !== 6 || t.some((x) => typeof x !== 'number' || !Number.isFinite(x))) {
            throw new FormatError(`${sp}.transform`, 'expected 6 finite numbers');
          }
          transform = t as Stroke['transform'];
        }
        records.strokes.push({
          ...(sRest as object),
          ...base(s, sp),
          ...parseRun(s, sp, page.id, notebook.id),
          tool: oneOf(s, 'tool', ['pen', 'highlighter'] as const, sp),
          color: str(s, 'color', sp),
          baseWidth: num(s, 'baseWidth', sp),
          ...(transform ? { transform } : {}),
        });
      });

      arr(hoverRaw ?? [], `${pp}.hover`).forEach((hv, hi) => {
        const hp = `${pp}.hover[${hi}]`;
        const h = obj(hv, hp);
        const { rev: _hr, ...hRest } = h;
        records.hover.push({
          ...(hRest as object),
          ...base(h, hp),
          ...parseRun(h, hp, page.id, notebook.id),
          prevStrokeId: idOrNull(h, 'prevStrokeId', hp),
          nextStrokeId: idOrNull(h, 'nextStrokeId', hp),
        });
      });
    });
  });
  return { doc: { format: FORMAT_ID, kind, exportedAt }, records };
}
